import React, { useState, useMemo } from 'react';
import { useAvailability } from '../hooks/useAvailability';
import Calendar from '../components/Calendar';

const DAYS_NL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

export default function SchoolCalendarPage() {
  const { availability, isConnected, isLoading } = useAvailability();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const selectedDateFormatted = useMemo(() => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate);
    return `${DAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]} ${d.getFullYear()}`;
  }, [selectedDate]);

  const selectedData = selectedDate ? availability[selectedDate] : null;

  if (isLoading) {
    return (
      <div className="container" style={{ textAlign: 'center', marginTop: '50px' }}>
        Beschikbaarheid laden...
      </div>
    );
  }

  return (
    <div className="container">
      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">👤</div>
          <div className="profile-info">
            <h2>Beschikbaarheid Agenda</h2>
            <p>Bekijk wanneer er beschikbaarheid is voor invalwerk</p>
          </div>
          <div className={`realtime-indicator ${isConnected ? '' : 'disconnected'}`} style={{ marginLeft: 'auto' }}>
            <div className="realtime-dot"></div>
            {isConnected ? 'Live updates' : 'Offline'}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
        <div>
          <Calendar
            availability={availability}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
          
          <div className="legend">
            <div className="legend-item">
              <div className="legend-dot available"></div>
              Beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot partial"></div>
              Gedeeltelijk beschikbaar
            </div>
          </div>

          <div style={{ marginTop: 20, padding: 15, background: '#e3f2fd', borderRadius: 8 }}>
            <strong>💡 Tip:</strong> De agenda wordt in realtime bijgewerkt. 
            Als er wijzigingen zijn, zie je die direct verschijnen.
          </div>
        </div>

        <div className="day-detail">
          {selectedDate ? (
            <>
              <h3>{selectedDateFormatted}</h3>
              
              {selectedData ? (
                <div style={{ marginTop: 20 }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 16px',
                    background: selectedData.status === 'available' ? '#e8f5e9' : '#fff3e0',
                    borderRadius: 8,
                    marginBottom: 20
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {selectedData.status === 'available' ? '✅' : '🕐'}
                    </span>
                    <span style={{ fontWeight: 500 }}>
                      {selectedData.status === 'available' ? 'Beschikbaar' : 'Gedeeltelijk beschikbaar'}
                    </span>
                  </div>

                  {selectedData.timeSlots && selectedData.timeSlots.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ fontWeight: 500, marginBottom: 10, display: 'block', color: '#666' }}>
                        Beschikbare tijden:
                      </label>
                      {selectedData.timeSlots.map((slot, index) => (
                        <div key={index} style={{
                          padding: '12px 16px',
                          background: '#f5f5f5',
                          borderRadius: 8,
                          marginBottom: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>🕒</span>
                          <span style={{ fontWeight: 500 }}>{slot.start}</span>
                          <span style={{ color: '#999' }}>tot</span>
                          <span style={{ fontWeight: 500 }}>{slot.end}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedData.notes && (
                    <div style={{
                      padding: 15,
                      background: '#fafafa',
                      borderRadius: 8,
                      borderLeft: '4px solid #4A90D9'
                    }}>
                      <label style={{ fontWeight: 500, marginBottom: 8, display: 'block', color: '#666' }}>
                        Notities:
                      </label>
                      <p style={{ margin: 0 }}>{selectedData.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  marginTop: 30, 
                  textAlign: 'center', 
                  color: '#999',
                  padding: 40 
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: 15 }}>📅</div>
                  <p>Geen beschikbaarheid op deze datum</p>
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: '#666',
              textAlign: 'center',
              padding: 40
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 15 }}>👆</div>
              <p>Selecteer een datum in de kalender om de beschikbaarheid te bekijken</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
